import Errored from './Errored';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../grafsync/grafsync';
import FindResourceDialog from '../ontology/FindResourceDialog';
import ResourceIriDisplay from '../ontology/ResourceIriDisplay';
import Loading from './Loading';
import ResourceLink from './ResourceLink';
import SecondaryButton from './SecondaryButton';
import TitledPanel from './TitledPanel';
import { globalStyles as styles } from './style/gstheme';
import { getLabelOrID, makeCancelable, sortObjects } from './utils/utils';
import BasicWrapper from './BasicWrapper';

class AnnotationPropertyDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      totalRelations: 0,
      resources: undefined,
      exclusionList: [],
      annotationData: undefined,
      loadingAnnotationStatistics: false,
      isErrored: undefined
    };
    this.cancellable = null;
    this.cancellable1 = null;
  }

  loadStatisticsData() {
    this.cancellable1 = makeCancelable(GS.export("ontologyannotationstatistics", this.props.id, this.props.graph));
    return this.cancellable1;
  }

  static getDerivedStateFromProps(props, state) {
    // We use the objects internal version counter set by GS to know if 
    const valuesLength = Array.isArray(props.values) ? props.values.length : (props.values ? 1 : 0);
    if (!state.isErrored && !state.isLoading && props.values && valuesLength !== state.totalRelations) {
      return {
        resources: undefined,
        isLoading: true,
        totalRelations: valuesLength
      };
    }
    return null;
  }

  componentDidUpdate() {
    const valuesLength = Array.isArray(this.props.values) ? this.props.values.length : (this.props.values ? 1 : 0);
    if (!this.state.isErrored && this.state.resources === undefined && this.props.values && valuesLength > 0) {
      this.collectResourceData();
    }
  }

  componentDidMount() {
    const valuesLength = Array.isArray(this.props.values) ? this.props.values.length : (this.props.values ? 1 : 0);
    if (this.props.values && valuesLength > 0) {
      this.collectResourceData();
    }
    else {
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ isLoading: false });
    }
  }

  getAnnotationStatistics = () => {
    this.setState({ loadingAnnotationStatistics: true }, async () => {
      try {
        GS.handle("annotation-statistics");
        const data = await this.loadStatisticsData().promise;
        if (this.cancellable1 && this.cancellable1.isCancelled()) return;
        const propertyList = data.graph.map(item => item.viewCountType);
        // Load all in one go
        try {
          GS.handle("annotation-statistics-loading");
          await this.loadData(propertyList).promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
        }
        catch (e) {
          if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
          return;
        }
        this.setState({ annotationData: data.graph });
      }
      catch (e) {
        if (!this.cancellable1 || (this.cancellable1 && !this.cancellable1.isCancelled())) this.setState({ isErrored: e });
      }
    });
  };

  loadData(iriData) {
    this.cancellable = makeCancelable(GS.loadData(iriData, {}, this.props.graph, false));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable1) this.cancellable1.cancel();
  }

  collectResourceData() {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    this.loadData(this.props.values, {})
      .promise
      .then(() => {
        GS.handle("annotation-loading");
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.processResources();
        this.setState({ isLoading: false });
      })
      .catch((e) => {
        if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
      });
  }

  processResources() {
    let valueList = Array.isArray(this.props.values) ? this.props.values : [this.props.values];
    let resources = valueList.map(id => {
      if (!GS.isLoaded(id, this.props.graph)) {
        return { id, notFound: true };
      }
      return GS.data(id, this.props.graph);
    });
    sortObjects(resources, 'label', this.props.defaultLang, 'und', false);
    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ resources });
  }

  monitorLinkUpdate = (iri, property) => {
    // This will re-sort if needed
    if (property === "label") this.processResources();
  };

  render() {

    const { id, isLoading, isReadOnly, graph, defaultLang, buildLink, values, onAddAnnotation,
      onToggleView, components, projectIRI, classes } = this.props;
    const { resources, annotationData, loadingAnnotationStatistics, isErrored } = this.state;

    if (isErrored) return <Errored dataTest="ap-error" data={isErrored} />;
    if (isLoading) return <Loading />;

    return (
      <TitledPanel dataTest="annotation-props" height="full" title="Annotation Properties" actions={
        !isReadOnly ?
          <FindResourceDialog
            source={id}
            dataTest="add-annotation-prop"
            defaultLang={defaultLang}
            ontologyIRI={id}
            allowMultiple={true}
            searchClass="http://www.w3.org/2002/07/owl#AnnotationProperty"
            includeSystemGraph={false}
            graph={graph}
            viewLang={defaultLang}
            exclude={values ? [...values] : []}
            onAddResource={onAddAnnotation} />
          :
          null
      }>
        <div data-test="listed-properties">
          {
            resources && resources.map(ap => {
              return <ResourceLink
                key={ap.id}
                dataTest={"annotation-prop"}
                id={ap.id}
                context={id}
                contextProperty="isDefinedBy"
                source={id}
                property="availableAnnotationProperty"
                link={buildLink}
                projectGraph={graph}
                graph={graph}
                isReadOnly={isReadOnly}
                updateEvent={this.monitorLinkUpdate}
                viewLang={defaultLang}
                handleNotAvailable={true}
                defaultLang={defaultLang} />;
            })
          }
        </div>
        {!isReadOnly && !annotationData && !loadingAnnotationStatistics &&
          <div style={{ marginTop: 24 }}>
            <SecondaryButton dataTest="compute-suggestions" onClick={this.getAnnotationStatistics}>Compute Suggestions to Add</SecondaryButton>
          </div>
        }
        {!annotationData && loadingAnnotationStatistics &&
          <div style={{ marginTop: 24 }}>
            <Typography variant="body2">Loading statistics ... please wait</Typography>
          </div>
        }
        {annotationData &&
          <div style={{ backgroundColor: 'white', borderRadius: 4, padding: 12, marginTop: 24 }}>
            <Typography className={classes.subMainHeading}>Computed Suggestions</Typography>
            <Table data-test="annotation-stats-viewer" style={{ maxWidth: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell component="th" className={classes.tableHeading}>Property</TableCell>
                  <TableCell component="th" className={classes.tableHeading} align="right">Usage Count</TableCell>
                  <TableCell component="th" className={classes.tableHeading}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {annotationData
                  .sort((a, b) => {
                    if (a.viewCount < b.viewCount) return 1;
                    if (a.viewCount > b.viewCount) return -1;
                    const aData = GS.data(a.viewCountType, graph, false);
                    const bData = GS.data(b.viewCountType, graph, false);
                    const aLabel = getLabelOrID(a, aData, "label", defaultLang);
                    const bLabel = getLabelOrID(b, bData, "label", defaultLang);
                    if (aLabel.toLowerCase() < bLabel.toLowerCase()) return -1;
                    if (bLabel.toLowerCase() < aLabel.toLowerCase()) return 1;
                    return 0;
                  })
                  .map((ad, rowIndex) => {
                    const usedAlready = values && values.includes(ad.viewCountType);
                    return (
                      <TableRow
                        key={"row-" + rowIndex}
                        data-test={"property-" + rowIndex}
                        classes={{
                          root: classes.stripedRow
                        }}>
                        <TableCell data-test="property" className={classes.tableCell}>
                          <div style={{ marginTop: 6, maxWidth: 500 }}>
                            <ResourceIriDisplay
                              id={ad.viewCountType}
                              components={components}
                              compact={true}
                              projectIRI={projectIRI}
                              handleNotAvailable={true}
                              context={id}
                              onLink={onToggleView}
                              graph={graph}
                              viewLang={defaultLang}
                              defaultLang={defaultLang} />
                          </div>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="right" style={{ verticalAlign: 'middle' }}>
                          <Typography data-test="usage-count" className={classes.tableCellText}>
                            {ad.viewCount}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell} align={"right"} style={{ verticalAlign: 'middle' }}>
                          {usedAlready ?
                            <Typography data-test="listed" className={classes.tableCellText} style={{ marginRight: 18 }}>Listed</Typography>
                            :
                            <BasicWrapper
                              component={SecondaryButton}
                              id={ad.viewCountType}
                              graph={graph}
                              dataTest="add-property"
                              onClick={() => onAddAnnotation(ad.viewCountType)}>Add</BasicWrapper>
                          }
                        </TableCell>
                      </TableRow>
                    );
                  }
                  )}
              </TableBody>
            </Table>
          </div>
        }
      </TitledPanel>
    );
  }
}

AnnotationPropertyDisplay.propTypes = {
  id: PropTypes.string.isRequired,
  buildLink: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  title: PropTypes.string,
  projectIRI: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  source: PropTypes.string,
  onToggleView: PropTypes.func.isRequired,
  components: PropTypes.array,
  onAddAnnotation: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired,
  values: PropTypes.array
};

export default withStyles(styles)(AnnotationPropertyDisplay);